import { Vec3, vec3, add } from '../core/math/vec3'
import { QUAT_IDENTITY, mulQuat, rotate, unrotate, fromAxisAngle } from '../core/math/quat'
import { Vessel, PartInstance, subtreeOf, partById } from '../core/vessel/vessel'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { Design, clonaDesign, compagne, gruppoDi } from './design'
import { Aggancio, agganciaRadiale } from './attach'

let contatore = 0
const nuovoId = (defId: string): string => `${defId}-${++contatore}`

function conVeicolo(d: Design, parts: PartInstance[], rootId = d.vessel.rootId): Design {
  return { ...d, vessel: { rootId, parts } }
}

/** La prima parte del progetto: si posa al centro e diventa la radice. */
export function aggiungiPrima(design: Design, catalog: PartCatalog, defId: string): Design {
  const def = partDef(catalog, defId)
  const id = nuovoId(defId)
  const parte: PartInstance = {
    id, defId, position: vec3(0, 0, 0), rotation: QUAT_IDENTITY,
    stage: 0, parentId: null, propellant: def.tank?.propellantMass ?? 0,
  }
  return conVeicolo(clonaDesign(design), [parte], id)
}

/** Aggancio assiale: sopra o sotto una parte gia montata. */
export function aggiungiAssiale(
  design: Design, catalog: PartCatalog, defId: string, aggancio: Aggancio, stage?: number,
): Design {
  const d = clonaDesign(design)
  const def = partDef(catalog, defId)
  const padre = partById(d.vessel, aggancio.parentId)
  const parte: PartInstance = {
    id: nuovoId(defId),
    defId,
    position: aggancio.position,
    rotation: aggancio.rotation,
    stage: stage ?? padre.stage,
    parentId: aggancio.parentId,
    propellant: def.tank?.propellantMass ?? 0,
  }
  return conVeicolo(d, [...d.vessel.parts, parte])
}

/**
 * Aggancio radiale con simmetria: N copie equidistanti attorno all'asse.
 * Le copie restano legate da un gruppo, cosi spostarne una le sposta tutte:
 * e l'unico comportamento che tiene un razzo bilanciato.
 */
export function aggiungiRadiale(
  design: Design, catalog: PartCatalog, defId: string,
  targetId: string, quota: number, azimut: number, simmetria = 1, stage?: number,
): Design {
  const d = clonaDesign(design)
  const def = partDef(catalog, defId)
  const padre = partById(d.vessel, targetId)
  const nuove: PartInstance[] = []

  for (let k = 0; k < Math.max(1, simmetria); k++) {
    const a = agganciaRadiale(
      d.vessel, catalog, defId, targetId, quota,
      azimut + (k * 2 * Math.PI) / Math.max(1, simmetria),
    )
    if (!a) return design
    nuove.push({
      id: nuovoId(defId),
      defId,
      position: a.position,
      rotation: a.rotation,
      stage: stage ?? padre.stage,
      parentId: targetId,
      propellant: def.tank?.propellantMass ?? 0,
    })
  }

  const gruppi = { ...d.symmetryGroups }
  if (nuove.length > 1) gruppi[`sim-${nuove[0]!.id}`] = nuove.map((p) => p.id)

  return { ...conVeicolo(d, [...d.vessel.parts, ...nuove]), symmetryGroups: gruppi }
}

/**
 * Toglie una parte, il suo sottoalbero e tutte le sue compagne di simmetria.
 * Togliere un serbatoio in mezzo porta via anche quello che ci stava sotto:
 * lasciare pezzi che fluttuano non e un'opzione.
 */
export function togliParte(design: Design, partId: string): Design {
  const d = clonaDesign(design)
  if (!d.vessel.parts.some((p) => p.id === partId)) return design

  const daTogliere = new Set<string>()
  for (const id of compagne(d, partId)) {
    if (!d.vessel.parts.some((p) => p.id === id)) continue
    for (const p of subtreeOf(d.vessel, id)) daTogliere.add(p.id)
  }

  const rimaste = d.vessel.parts.filter((p) => !daTogliere.has(p.id))
  const gruppi: Record<string, string[]> = {}
  for (const [g, membri] of Object.entries(d.symmetryGroups)) {
    const vivi = membri.filter((m) => !daTogliere.has(m))
    if (vivi.length > 1) gruppi[g] = vivi
  }

  const radice = rimaste.some((p) => p.id === d.vessel.rootId) ? d.vessel.rootId : (rimaste[0]?.id ?? '')
  return { ...conVeicolo(d, rimaste, radice), symmetryGroups: gruppi }
}

/**
 * Sposta una parte di uno scostamento libero.
 *
 * E QUESTA la funzione che permette di mettere il motore fuori asse, cioe la
 * cosa che l'intero progetto esiste per mostrare. Sulle parti in simmetria lo
 * scostamento viene riespresso nel riferimento di ciascuna, cosi la simmetria
 * non si rompe.
 */
export function spostaParte(design: Design, partId: string, offset: Vec3): Design {
  const d = clonaDesign(design)
  const scelta = d.vessel.parts.find((p) => p.id === partId)
  if (!scelta) return design

  const locale = unrotate(scelta.rotation, offset)
  const gruppo = gruppoDi(d, partId)

  return conVeicolo(d, d.vessel.parts.map((p) => {
    if (p.id === partId) return { ...p, position: add(p.position, offset) }
    if (gruppo && d.symmetryGroups[gruppo]!.includes(p.id)) {
      return { ...p, position: add(p.position, rotate(p.rotation, locale)) }
    }
    return p
  }))
}

/** Ruota una parte attorno a un asse del veicolo: serve a inclinare un ugello. */
export function ruotaParte(design: Design, partId: string, asse: Vec3, angolo: number): Design {
  const d = clonaDesign(design)
  const scelta = d.vessel.parts.find((p) => p.id === partId)
  if (!scelta) return design

  // l'asse si esprime nel riferimento della parte scelta e si riusa uguale su
  // tutte le compagne: cosi in simmetria ognuna si inclina "verso l'esterno"
  // allo stesso modo, invece di inclinarsi tutte nella stessa direzione
  const asseLocale = unrotate(scelta.rotation, asse)
  const membri = new Set(compagne(d, partId))

  return conVeicolo(d, d.vessel.parts.map((p) =>
    membri.has(p.id)
      ? { ...p, rotation: mulQuat(p.rotation, fromAxisAngle(asseLocale, angolo)) }
      : p))
}

export function assegnaStadio(design: Design, partId: string, stage: number): Design {
  const d = clonaDesign(design)
  const membri = new Set(compagne(d, partId))
  return conVeicolo(d, d.vessel.parts.map((p) =>
    membri.has(p.id) ? { ...p, stage: Math.max(0, Math.round(stage)) } : p))
}

/** Compatta gli stadi togliendo i buchi: 0, 3, 7 diventano 0, 1, 2. */
export function rinumeraStadi(design: Design): Design {
  const d = clonaDesign(design)
  const usati = [...new Set(d.vessel.parts.map((p) => p.stage))].sort((a, b) => a - b)
  const mappa = new Map(usati.map((s, i) => [s, i]))
  return conVeicolo(d, d.vessel.parts.map((p) => ({ ...p, stage: mappa.get(p.stage)! })))
}

export const contaParti = (design: Design): number => design.vessel.parts.length
